import React, { useState } from 'react';
import { Search, Plus, Mail, Eye } from 'lucide-react';
import { EmailEditor } from './EmailEditor';

/**
 * Template picker component for selecting email templates
 * Shows templates in a grid with search functionality
 */
export function EmailTemplatePicker({ 
  templates = [], 
  selectedTemplateId,
  onSelect,
  onCreateNew,
  showPreview = false
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const filteredTemplates = templates.filter(template => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      template.name?.toLowerCase().includes(query) ||
      template.subject?.toLowerCase().includes(query) ||
      template.htmlContent?.toLowerCase().includes(query)
    );
  });

  const handlePreview = (template) => {
    setPreviewTemplate(template);
  };

  return (
    <div className="space-y-4">
      {/* Search and Create */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
          />
        </div>
        {onCreateNew && (
          <button
            onClick={onCreateNew}
            className="flex items-center gap-2 px-4 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
          >
            <Plus size={18} />
            New Template
          </button>
        )}
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">
            {searchQuery ? 'No templates found' : 'No templates yet'}
          </p>
          {onCreateNew && !searchQuery && (
            <button
              onClick={onCreateNew}
              className="px-6 py-2 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition"
            >
              Create First Template
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => {
            const isSelected = selectedTemplateId === template.id;
            const contentPreview = template.htmlContent?.replace(/<[^>]*>/g, '').substring(0, 100) || 'No content';

            return (
              <div
                key={template.id}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-[#c5a059] bg-[#c5a059]/5'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
                onClick={() => onSelect(template.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-gray-800 text-sm truncate flex-1">
                    {template.name || 'Untitled Template'}
                  </h4>
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-[#c5a059] flex-shrink-0 ml-2 mt-1" />
                  )}
                </div>
                <p className="text-xs text-gray-600 mb-2 line-clamp-1">
                  {template.subject || 'No subject'}
                </p>
                <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                  {contentPreview}
                </p>
                <div className="flex items-center gap-2">
                  {showPreview && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreview(template);
                      }}
                      className="flex items-center gap-1 text-xs text-gray-600 hover:text-[#c5a059] transition"
                    >
                      <Eye size={14} />
                      Preview
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewTemplate(null)}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">{previewTemplate.name}</h3>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Subject:</p>
                <p className="text-gray-800">{previewTemplate.subject || 'No subject'}</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewTemplate.htmlContent || '' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}











